import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from './config';
import logger from './utils/logger';
import { FiLock, FiEye, FiEyeOff, FiArrowRight, FiMail } from "react-icons/fi";
import './App.css';

function InvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingInfo, setIsFetchingInfo] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');

  useEffect(() => {
    setIsFetchingInfo(true);
    axios.get(`${API_BASE_URL}/auth/invite-info/${token}`)
      .then(response => {
        const data = response.data;
        if (data.valid) {
          setEmail(data.email);
          setTokenValid(true);
        } else if (data.expired) {
          setTokenError('This invite link has expired. Please ask your admin for a new one.');
        } else if (data.used) {
          setTokenError('This invite link has already been used. Please log in instead.');
        } else {
          setTokenError('This invite link is no longer valid.');
        }
      })
      .catch(() => {
        setTokenError('Invalid invite link. Please check the URL or ask your admin for a new invite.');
      })
      .finally(() => {
        setIsFetchingInfo(false);
      });
  }, [token]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (password.length < 6) {
      setErrorMessage('Password must be at least 6 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    setIsLoading(true);

    axios.post(`${API_BASE_URL}/auth/accept-invite`, { token, password })
      .then(response => {
        const { accessToken, refreshToken, user } = response.data;
        const orgId = user?.companyId;

        localStorage.setItem('authToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('orgId', orgId || '');
        localStorage.setItem('email', user.email);
        localStorage.setItem('userName', user.name || user.email.split('@')[0] || 'User');
        localStorage.setItem('roles', JSON.stringify(user.roles || []));
        localStorage.setItem('userId', user.id || '');

        navigate('/dashboard');
      })
      .catch(error => {
        const msg = error.response?.data?.error || 'Failed to accept invite. Please try again.';
        setErrorMessage(msg);
        setIsLoading(false);
      });
  };

  const styles = `
    .login-container {
      display: flex;
      height: 100vh;
      width: 100%;
      overflow: hidden;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      box-sizing: border-box;
    }

    .login-container * {
      box-sizing: border-box;
    }

    .login-left {
      flex: 1;
      background: #0c0c0c;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      padding: 40px;
      position: relative;
    }

    .login-left::before {
      content: "";
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      background: linear-gradient(135deg, rgba(255, 214, 0, 0.08) 0%, rgba(255, 214, 0, 0.03) 50%, transparent 100%);
      opacity: 1;
    }

    .login-left::after {
      content: "";
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      background-image:
        linear-gradient(45deg, rgba(255, 214, 0, 0.03) 25%, transparent 25%),
        linear-gradient(-45deg, rgba(255, 214, 0, 0.03) 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, rgba(255, 214, 0, 0.03) 75%),
        linear-gradient(-45deg, transparent 75%, rgba(255, 214, 0, 0.03) 75%);
      background-size: 60px 60px;
      background-position: 0 0, 0 30px, 30px -30px, -30px 0px;
      opacity: 0.4;
    }

    .brand-content {
      position: relative;
      z-index: 2;
      max-width: 400px;
    }

    .brand-title {
      font-size: 48px;
      font-weight: 800;
      margin-bottom: 10px;
      letter-spacing: -0.5px;
    }

    .brand-tagline {
      font-size: 18px;
      color: #fafafa;
      font-weight: 500;
      margin-bottom: 20px;
    }

    .brand-divider {
      width: 60px;
      height: 4px;
      background: #FFD600;
      margin: 0 auto 20px;
      border-radius: 2px;
    }

    .brand-subtext {
      font-size: 14px;
      color: #a3a3a3;
      font-weight: 400;
    }

    .login-right {
      flex: 1;
      background: #0c0c0c;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 40px;
    }

    .login-box {
      width: 100%;
      max-width: 420px;
    }

    .login-header {
      margin-bottom: 30px;
      text-align: left;
    }

    .login-header h2 {
      font-size: 28px;
      font-weight: 700;
      color: #fafafa;
      margin-bottom: 8px;
    }

    .text-muted {
      color: #a3a3a3;
      font-size: 14px;
    }

    .login-form {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    .form-group {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .form-group label {
      font-size: 14px;
      font-weight: 600;
      color: #d4d4d4;
    }

    .input-wrapper {
      position: relative;
      display: flex;
      align-items: center;
      width: 100%;
    }

    .login-form .input-icon {
      position: absolute;
      left: 16px;
      color: #a3a3a3;
      font-size: 20px;
      z-index: 10;
      pointer-events: none;
    }

    .login-form .form-input {
      width: 100%;
      height: 48px;
      padding: 10px 16px 10px 48px !important;
      border: 1px solid #2a2a2a;
      border-radius: 8px;
      font-size: 15px;
      color: #fafafa;
      background-color: #141414;
      transition: all 0.2s ease;
      outline: none;
    }

    .login-form .form-input::placeholder {
      color: #6b7280;
    }

    .login-form .form-input:focus {
      border-color: #FFD600;
      background-color: #1a1a1a;
      box-shadow: 0 0 0 4px rgba(255, 214, 0, 0.15);
    }

    .login-form .form-input:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .password-toggle {
      position: absolute;
      right: 16px;
      background: none;
      border: none;
      cursor: pointer;
      color: #a3a3a3;
      font-size: 20px;
      display: flex;
      align-items: center;
      padding: 0;
      z-index: 10;
    }

    .password-toggle:hover {
      color: #d4d4d4;
    }

    .btn-login {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 8px;
      width: 100%;
      height: 48px;
      background: #FFD600;
      color: black;
      border: none;
      border-radius: 8px;
      font-size: 16px;
      font-weight: 700;
      cursor: pointer;
      transition: background 0.2s;
      margin-top: 10px;
    }

    .btn-login:hover {
      background: #FFEA00;
      transform: translateY(-1px);
      box-shadow: 0 4px 6px rgba(0,0,0,0.2);
    }

    .btn-login:disabled {
      background: #ffe59e;
      cursor: not-allowed;
    }

    .loader-spinner {
      width: 20px;
      height: 20px;
      border: 2px solid #000;
      border-bottom-color: transparent;
      border-radius: 50%;
      display: inline-block;
      box-sizing: border-box;
      animation: rotation 1s linear infinite;
    }

    @keyframes rotation {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    .alert-error-box {
      background-color: rgba(239, 68, 68, 0.2);
      border: 1px solid rgba(239, 68, 68, 0.4);
      color: #fca5a5;
      padding: 12px;
      border-radius: 8px;
      font-size: 14px;
      text-align: center;
    }

    .token-error-box {
      background-color: rgba(239, 68, 68, 0.1);
      border: 1px solid rgba(239, 68, 68, 0.3);
      color: #fca5a5;
      padding: 24px;
      border-radius: 12px;
      font-size: 15px;
      text-align: center;
      line-height: 1.6;
    }

    .login-footer {
      margin-top: 40px;
      text-align: center;
    }

    .login-footer p {
      color: #737373;
      font-size: 12px;
    }

    @media (max-width: 768px) {
      .login-container {
        flex-direction: column;
      }

      .login-left {
        display: none;
      }

      .login-right {
        padding: 20px;
      }
    }
  `;

  return (
    <div className="login-container">
      <style>{styles}</style>

      {/* LEFT SIDE - BRANDING */}
      <div className="login-left">
        <div className="brand-content">
          <h1 className="brand-title">
            <span style={{color: '#fafafa'}}>Sign</span>
            <span style={{background: 'linear-gradient(135deg, #FFD600 0%, #FFAB00 100%)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'}}>Tada</span>
          </h1>
          <p className="brand-tagline">Where Documents Meet Trust.</p>
          <div className="brand-divider"></div>
          <p className="brand-subtext">Secure. Legally Binding. Fast.</p>
        </div>
      </div>

      {/* RIGHT SIDE - FORM */}
      <div className="login-right">
        <div className="login-box">
          {isFetchingInfo ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <span className="loader-spinner" style={{ width: 32, height: 32, borderColor: '#FFD600', borderBottomColor: 'transparent' }}></span>
              <p className="text-muted" style={{ marginTop: 16 }}>Loading invite details...</p>
            </div>
          ) : !tokenValid ? (
            <>
              <div className="login-header">
                <h2>Invite Link</h2>
              </div>
              <div className="token-error-box">{tokenError}</div>
              <div style={{ textAlign: 'center', marginTop: 24 }}>
                <a href="/login" style={{ color: '#fbbf24', textDecoration: 'none', fontWeight: 600, fontSize: 14 }}>
                  Go to Login
                </a>
              </div>
              <div className="login-footer">
                <p>&copy; {new Date().getFullYear()} SignTada Inc.</p>
              </div>
            </>
          ) : (
            <>
              <div className="login-header">
                <h2>Set Your Password</h2>
                <p className="text-muted">You've been invited to join SignTada. Set your password to activate your account.</p>
              </div>

              <form onSubmit={handleSubmit} className="login-form">
                {/* Email (read-only) */}
                <div className="form-group">
                  <label>Email Address</label>
                  <div className="input-wrapper">
                    <FiMail className="input-icon" />
                    <input
                      type="email"
                      value={email}
                      disabled
                      className="form-input"
                    />
                  </div>
                </div>

                {/* Password */}
                <div className="form-group">
                  <label>Password</label>
                  <div className="input-wrapper">
                    <FiLock className="input-icon" />
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Create a password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="form-input"
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="form-group">
                  <label>Confirm Password</label>
                  <div className="input-wrapper">
                    <FiLock className="input-icon" />
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="form-input"
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </div>

                {/* Error Message */}
                {errorMessage && (
                  <div className="alert-error-box">
                    {errorMessage}
                  </div>
                )}

                {/* Submit Button */}
                <button type="submit" className="btn-login" disabled={isLoading}>
                  {isLoading ? (
                    <span className="loader-spinner"></span>
                  ) : (
                    <>Activate Account <FiArrowRight /></>
                  )}
                </button>
              </form>

              <div className="login-footer">
                <p>&copy; {new Date().getFullYear()} SignTada Inc.</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default InvitePage;
